import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ActivityIndicator, Alert, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTradeSession } from '../../hooks/useTradeSession';
import { usePeerAlbum } from '../../hooks/usePeerAlbum';
import { peerNeedsSticker } from './utils/tradeSuggestion';
import { useUserStore } from '../../store/userStore';
import { useAlbumStore } from '../../store/albumStore';
import {
  cancelTradeSession,
  confirmTrade,
  updateMySelection,
} from '../../services/tradeSessionService';
import { useTradeStore } from '../../store/tradeStore';
import { allStickers, allStickersWithCocaCola } from '../album/data/albumCatalog';
import { StickerCheckRow } from './components/StickerCheckRow';
import { CheckIcon } from './components/TradeIcons';
import { colors, radii, spacing } from '../../constants/theme';
import { track } from '../../services/analytics';
import type { TradeStackParamList } from '../../types/navigation';
import type { TradeRole } from './types';

const stickerById = new Map(allStickers.map((s) => [s.id, s]));

// Orden canónico del álbum: Grupo A, B, C, ..., L, especiales (FWC), Coca-Cola.
const catalogIndex = new Map<string, number>(
  allStickersWithCocaCola.map((s, i) => [s.id, i]),
);

function compareByCatalog(a: string, b: string): number {
  return (
    (catalogIndex.get(a) ?? Number.MAX_SAFE_INTEGER) -
    (catalogIndex.get(b) ?? Number.MAX_SAFE_INTEGER)
  );
}

function rowFor(stickerId: string) {
  const sticker = stickerById.get(stickerId);
  return {
    displayCode: sticker?.displayCode ?? stickerId,
    label: sticker?.label || sticker?.countryName || 'Jugador',
    countryName: sticker?.countryName,
    countryFlag: sticker?.countryFlag,
  };
}

function dedupSorted(ids: string[]): string[] {
  return Array.from(new Set(ids)).sort(compareByCatalog);
}

export function TradeSelectScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<TradeStackParamList>>();
  const route = useRoute<RouteProp<TradeStackParamList, 'TradeSelect'>>();
  const { sessionId, role } = route.params;
  const session = useTradeSession(sessionId);
  const repeatedCounts = useAlbumStore((s) => s.repeatedCounts);
  const statuses = useAlbumStore((s) => s.statuses);
  const setActive = useTradeStore((s) => s.setActive);
  const clearTrade = useTradeStore((s) => s.clear);
  const user = useUserStore((s) => s.user);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (sessionId) setActive(sessionId, role);
  }, [sessionId, role, setActive]);

  useEffect(() => {
    if (!session) return;
    if (session.status === 'waiting') {
      // Resumimos desde reload pero la sesión todavía no tiene guest pareado:
      // si soy host, volvemos a la pantalla de espera (QR/link). Si soy guest,
      // la sesión no debería estar en waiting — limpiar y volver al home.
      if (role === 'host') {
        navigation.replace('TradeHost', { sessionId: session.id });
      } else {
        clearTrade();
        navigation.replace('TradeHome');
      }
      return;
    }
    if (session.status === 'completed') {
      navigation.replace('TradeComplete', { sessionId: session.id });
      return;
    }
    if (session.status === 'cancelled' || session.status === 'expired') {
      Alert.alert('Sesión finalizada', session.failureReason ?? 'La sesión se canceló.');
      clearTrade();
      navigation.replace('TradeHome');
      return;
    }
    // Tras reload, si ya confirmé, saltar a TradeReview para que se dispare el
    // commit automático cuando el otro confirme. Sin esto, el user queda atrapado
    // en TradeSelect con "Ya confirmaste" sin avanzar.
    const myConfirmedAtNow =
      role === 'host' ? session.hostConfirmedAt : session.guestConfirmedAt;
    if (myConfirmedAtNow) {
      navigation.replace('TradeReview', { sessionId: session.id, role });
    }
  }, [session, navigation, clearTrade, role]);

  const myOffer = role === 'host' ? session?.hostStickers ?? [] : session?.guestStickers ?? [];
  const peerOffer = role === 'host' ? session?.guestStickers ?? [] : session?.hostStickers ?? [];
  const peerName = role === 'host' ? session?.guestName : session?.hostName;
  const peerUid = role === 'host' ? session?.guestUid ?? null : session?.hostUid ?? null;
  const myConfirmedAt = role === 'host' ? session?.hostConfirmedAt : session?.guestConfirmedAt;
  const peerConfirmedAt = role === 'host' ? session?.guestConfirmedAt : session?.hostConfirmedAt;
  const { album: peerAlbum, loaded: peerAlbumLoaded } = usePeerAlbum(peerUid);

  const [draftMine, setDraftMine] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched && session) {
      setDraftMine(dedupSorted(myOffer));
    }
  }, [session, myOffer, touched]);

  const myRepeatedIds = useMemo(
    () =>
      Object.entries(repeatedCounts)
        .filter(([, c]) => (c ?? 0) > 0)
        .map(([id]) => id)
        .sort(compareByCatalog),
    [repeatedCounts],
  );

  // Solo se ofrecen repetidas que el peer realmente necesite. Si el peer aún
  // no tiene album cargado o no tiene documento, no mostramos nada — preferimos
  // pedirle al usuario que espere antes que ofrecerle figus que el peer ya tiene.
  const myCandidates = useMemo(() => {
    if (!peerAlbumLoaded) return [];
    if (!peerAlbum) return [];
    return myRepeatedIds.filter((id) => peerNeedsSticker(peerAlbum.statuses, id));
  }, [myRepeatedIds, peerAlbum, peerAlbumLoaded]);

  // Candidatos del peer: sus repetidas que YO necesito. Se muestran todos en
  // "Lo que recibo" (gris); los que el peer marcó aparecen en verde via overlay.
  const peerRepeatedIds = useMemo(() => {
    if (!peerAlbum) return [] as string[];
    return Object.entries(peerAlbum.repeatedCounts ?? {})
      .filter(([, c]) => (c ?? 0) > 0)
      .map(([id]) => id)
      .sort(compareByCatalog);
  }, [peerAlbum]);

  const peerCandidates = useMemo(() => {
    if (!peerAlbumLoaded || !peerAlbum) return [] as string[];
    return peerRepeatedIds.filter((id) => peerNeedsSticker(statuses, id));
  }, [peerRepeatedIds, peerAlbum, peerAlbumLoaded, statuses]);

  const peerOfferSet = useMemo(() => new Set(peerOffer), [peerOffer]);

  // Lo que efectivamente recibo: la selección del peer ∩ candidatos útiles.
  // Sirve para el count y la validación de Confirmar.
  const peerSelectedUseful = useMemo(
    () => peerCandidates.filter((id) => peerOfferSet.has(id)),
    [peerCandidates, peerOfferSet],
  );

  // Limpia draft de ids que dejaron de ser repetidas o que el peer ya no necesita.
  useEffect(() => {
    if (draftMine.length === 0) return;
    const candSet = new Set(myCandidates);
    const cleaned = draftMine.filter((id) => candSet.has(id));
    if (cleaned.length !== draftMine.length) {
      setDraftMine(cleaned);
      setTouched(true);
    }
  }, [myCandidates, draftMine]);

  const noMatchAtAll =
    peerAlbumLoaded &&
    !!peerAlbum &&
    myCandidates.length === 0 &&
    peerCandidates.length === 0;

  const handleToggleMine = useCallback((id: string) => {
    setTouched(true);
    setDraftMine((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set).sort(compareByCatalog);
    });
  }, []);

  const draftDiffersFromRemote = useMemo(() => {
    const a = dedupSorted(myOffer);
    const b = dedupSorted(draftMine);
    if (a.length !== b.length) return true;
    return a.some((id, i) => id !== b[i]);
  }, [myOffer, draftMine]);

  const handleSyncSelection = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    setWorking(true);
    try {
      await updateMySelection(sessionId, role, draftMine);
      setTouched(false);
      return true;
    } catch (e) {
      console.error('[trade] update selection failed', e);
      Alert.alert('Error', 'No pudimos guardar tu selección. Probá de nuevo.');
      return false;
    } finally {
      setWorking(false);
    }
  }, [sessionId, role, draftMine]);

  // Auto-save: tras 600ms de inactividad, persiste el draft a Firestore para
  // que el peer vea los toggles en tiempo real. No corre antes de la primera
  // interacción (touched=false) ni si no hay diferencias con el remoto.
  useEffect(() => {
    if (!sessionId || !touched) return;
    if (!draftDiffersFromRemote) return;
    const t = setTimeout(() => {
      handleSyncSelection().catch(() => {/* manejo de error en handler */});
    }, 600);
    return () => clearTimeout(t);
  }, [draftMine, touched, sessionId, draftDiffersFromRemote, handleSyncSelection]);

  const handleConfirm = useCallback(async () => {
    if (!sessionId) return;
    if (draftDiffersFromRemote) {
      const synced = await handleSyncSelection();
      if (!synced) return;
    }
    if (draftMine.length === 0 && peerSelectedUseful.length === 0) {
      Alert.alert('Falta seleccionar', 'Marcá al menos una figu para intercambiar.');
      return;
    }
    setWorking(true);
    try {
      await confirmTrade(sessionId, role);
      track({
        name: 'trade_confirmed',
        params: { sessionId, role, givesCount: draftMine.length, receivesCount: peerSelectedUseful.length },
      });
      navigation.replace('TradeReview', { sessionId, role });
    } catch (e) {
      console.error('[trade] confirm failed', e);
      Alert.alert('Error', 'No pudimos confirmar. Probá de nuevo.');
    } finally {
      setWorking(false);
    }
  }, [sessionId, role, draftMine, peerSelectedUseful.length, draftDiffersFromRemote, handleSyncSelection, navigation]);

  const handleCancel = useCallback(async () => {
    if (!sessionId) {
      navigation.replace('TradeHome');
      return;
    }
    setWorking(true);
    try {
      await cancelTradeSession(sessionId, `${role}_cancelled`);
      track({ name: 'trade_cancelled', params: { sessionId, reason: `${role}_cancelled` } });
      clearTrade();
      navigation.replace('TradeHome');
    } catch (e) {
      console.error('[trade] cancel failed', e);
      Alert.alert('Error', 'No pudimos cancelar. Probá de nuevo.');
    } finally {
      setWorking(false);
    }
  }, [sessionId, role, navigation, clearTrade]);

  if (!user || !session) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.primary} />
      </View>
    );
  }

  const sessionExpired = session.expiresAt < Date.now();

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[styles.content, { paddingBottom: spacing.xl + insets.bottom }]}
    >
      <View style={styles.header}>
        <Text style={styles.eyebrow}>Intercambio con {peerName ?? '…'}</Text>
        <Text style={styles.title}>Marcá las figus</Text>
        <Text style={styles.description}>
          A la izquierda, las que vos das. A la derecha, las que recibís. Confirmen los dos para cerrar.
        </Text>
      </View>

      {sessionExpired ? (
        <View style={styles.bannerWarn}>
          <Text style={styles.bannerWarnText}>La sesión expiró. Cancelá y armá una nueva.</Text>
        </View>
      ) : null}

      {noMatchAtAll ? (
        <View style={styles.bannerWarn}>
          <Text style={styles.bannerWarnText}>
            No hay figus útiles entre ustedes dos. Cancelá y probá con otra persona.
          </Text>
        </View>
      ) : null}

      <Section title="Lo que doy (útiles para el otro)" countSelected={draftMine.length}>
        {!peerAlbumLoaded ? (
          <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.md }} />
        ) : !peerAlbum ? (
          <Text style={styles.emptyText}>
            Esperando que el otro marque sus figuritas. Apenas el otro abra el álbum, vas a ver acá qué le sirve.
          </Text>
        ) : myRepeatedIds.length === 0 ? (
          <Text style={styles.emptyText}>No tenés repetidas todavía. Cargalas desde el álbum.</Text>
        ) : myCandidates.length === 0 ? (
          <Text style={styles.emptyText}>
            Ninguna de tus repetidas le sirve al otro. Probá con otra persona.
          </Text>
        ) : (
          myCandidates.map((id) => {
            const r = rowFor(id);
            return (
              <StickerCheckRow
                key={id}
                stickerId={id}
                displayCode={r.displayCode}
                label={r.label}
                countryName={r.countryName}
                countryFlag={r.countryFlag}
                selected={draftMine.includes(id)}
                onToggle={handleToggleMine}
              />
            );
          })
        )}
      </Section>

      <Section title="Lo que recibo" countSelected={peerSelectedUseful.length}>
        {!peerAlbumLoaded ? (
          <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.md }} />
        ) : !peerAlbum ? (
          <Text style={styles.emptyText}>
            Esperando que el otro marque sus figuritas.
          </Text>
        ) : peerCandidates.length === 0 ? (
          <Text style={styles.emptyText}>
            El otro no tiene repetidas que te sirvan.
          </Text>
        ) : (
          peerCandidates.map((id) => {
            const r = rowFor(id);
            return (
              <StickerCheckRow
                key={id}
                stickerId={id}
                displayCode={r.displayCode}
                label={r.label}
                countryName={r.countryName}
                countryFlag={r.countryFlag}
                selected={peerOfferSet.has(id)}
                disabled
                onToggle={() => {}}
              />
            );
          })
        )}
      </Section>

      <View style={styles.summary}>
        <Text style={styles.summaryLine}>
          Doy: <Text style={styles.summaryStrong}>{draftMine.length}</Text>
        </Text>
        <Text style={styles.summaryLine}>
          Recibo: <Text style={styles.summaryStrong}>{peerSelectedUseful.length}</Text>
        </Text>
      </View>

      <View style={styles.actions}>
        <Pressable
          onPress={handleConfirm}
          disabled={working || sessionExpired}
          style={({ pressed }) => [
            styles.cta,
            styles.ctaPrimary,
            pressed && styles.pressed,
            (working || sessionExpired) && styles.disabled,
          ]}
        >
          {working ? <ActivityIndicator color="#001A0A" /> : (
            <Text style={styles.ctaText}>
              {myConfirmedAt && !draftDiffersFromRemote ? 'Ya confirmaste' : 'Confirmar'}
            </Text>
          )}
        </Pressable>

        <View style={styles.peerStatusRow}>
          {peerConfirmedAt ? <CheckIcon size={14} color={colors.primary} /> : null}
          <Text style={styles.peerStatus}>
            {peerConfirmedAt ? 'El otro ya confirmó' : 'El otro todavía no confirmó'}
          </Text>
        </View>
      </View>

      <Pressable
        onPress={handleCancel}
        disabled={working}
        style={({ pressed }) => [styles.cancelBtn, pressed && styles.pressed]}
      >
        <Text style={styles.cancelText}>Cancelar intercambio</Text>
      </Pressable>
    </ScrollView>
  );
}

type SectionProps = {
  title: string;
  countSelected: number;
  children: ReactNode;
};

function Section({ title, countSelected, children }: SectionProps) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.badge}>
          <Text style={styles.badgeText}>{countSelected}</Text>
        </View>
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  center: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    backgroundColor: colors.card,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: radii.lg,
    padding: spacing.lg,
  },
  eyebrow: {
    color: colors.accent,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: spacing.sm,
  },
  title: {
    color: colors.text,
    fontSize: 26,
    fontWeight: '900',
    marginBottom: spacing.sm,
  },
  description: {
    color: colors.textMuted,
    fontSize: 14,
    lineHeight: 22,
  },
  section: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: radii.lg,
    padding: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  badge: {
    backgroundColor: colors.background,
    borderColor: colors.primary,
    borderWidth: 1,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
  },
  badgeText: {
    color: colors.primary,
    fontWeight: '800',
    fontSize: 13,
  },
  emptyText: {
    color: colors.textMuted,
    fontSize: 13,
    paddingVertical: spacing.sm,
  },
  summary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: colors.card,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: radii.md,
    padding: spacing.md,
  },
  summaryLine: {
    color: colors.textMuted,
    fontWeight: '600',
  },
  summaryStrong: {
    color: colors.accent,
    fontWeight: '900',
  },
  actions: {
    gap: spacing.sm,
  },
  cta: {
    borderRadius: radii.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
    borderWidth: 1,
  },
  ctaPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  ctaSecondary: {
    backgroundColor: colors.card,
    borderColor: colors.accent,
  },
  ctaText: {
    color: '#001A0A',
    fontSize: 16,
    fontWeight: '900',
  },
  ctaTextSecondary: {
    color: colors.accent,
  },
  pressed: {
    opacity: 0.85,
  },
  disabled: {
    opacity: 0.55,
  },
  peerStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  peerStatus: {
    color: colors.textMuted,
    fontSize: 13,
    fontWeight: '700',
  },
  cancelBtn: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  cancelText: {
    color: colors.danger,
    fontWeight: '700',
  },
  bannerWarn: {
    backgroundColor: '#3A1620',
    borderColor: colors.danger,
    borderWidth: 1,
    borderRadius: radii.md,
    padding: spacing.md,
  },
  bannerWarnText: {
    color: colors.danger,
    fontWeight: '700',
  },
  bannerInfo: {
    backgroundColor: '#1F2A36',
    borderColor: colors.secondary,
    borderWidth: 1,
    borderRadius: radii.md,
    padding: spacing.md,
  },
  bannerInfoText: {
    color: colors.text,
    fontWeight: '600',
  },
});
